<template>
  <header class="sm-header">
    <div class="sm-header-inner">
      <a
        :href="provider.website_url || '/'"
        class="sm-header__logo"
        target="_blank"
        rel="noreferrer"
      >
        <img-loader :src="logo" />
      </a>
      <div
        v-if="!isMobile"
        class="sm-header__links"
        :class="{
          single: links.length == 1,
          'admin-sec': !isAdmin && user.privileged_user,
          beta: provider.client_level == 1
        }"
      >
        <a
          v-for="link in links"
          :key="link.name"
          :href="link.href || link.name"
          class="sm-header__links-item"
          :class="{
            current: checkLinkCurrent(link),
            disabled: checkLinkInactive(link),
            hidden: link.roles && !link.roles.includes(userAccount.role)
          }"
          @click.prevent="goTo(link.href || `/${link.name}`)"
        >
          <include-svg :name="link.icon" />
          <span class="sm-header__links-item-title sm-txt-strong">{{
            link.title || toUpper(link.name)
          }}</span>
        </a>
      </div>

      <template v-if="!isMobile">
        <div v-if="!isAdmin" class="sm-header__controls">
          <a
            v-for="link in controlLinks"
            :key="link.name"
            :href="link.href || link.name"
            class="sm-header__controls-link"
            :class="{
              disabled: link.disabled
            }"
            v-if="link.name === 'announcements' && link.roles ? link.roles.includes(userAccount.role) : true"
            @click.prevent="goTo(link.href || `/${link.name}`)"
          >
            <include-svg :name="link.icon" />
            <span v-if="link.notices" class="sm-white">{{ link.notices }}</span>
          </a>
          <a
            href="#"
            class="sm-header__controls-link"
            @click.prevent="showQRPopup"
          >
            <include-svg :name="'icon-mobile-qr'" />
          </a>
          <div v-if="showQR" v-click-outside="hideQRPopup" class="sm-header__mobile-qr">
            <div class="sm-header__mobile-qr--popup">
              <div class="sm-header__mobile-qr--img">
                <img-loader :src="require('../assets/images/mobile-app-qr-code.png')" />
              </div>
              <div class="sm-header__mobile-qr--text">
                <h3>Download App</h3>
                <p>Scan to download SphereMail app</p>
                <p>iOS & Android</p>
              </div>
            </div>
          </div>
          <a
            v-if="showErrorsLink"
            href="errors"
            class="sm-header__controls-link"
            @click.prevent="goTo('/errors')"
          >
            <include-svg :name="'icon-expl'" />
          </a>

          <div class="sm-header__controls-dropdown">
            <button
              class="sm-header__controls-dropdown-btn"
              :class="menuPopup ? 'active' : ''"
              @click="togglePopup()"
            >
              <p>
                <text-truncate
                  class="sm-header__controls-dropdown-btn-name sm-txt-strong"
                  :text="name"
                  :maxWidth="'200px'"
                />
                <span
                  v-if="
                    userAccount &&
                      userAccount.role == 'manager' &&
                      provider &&
                      provider.client_level
                  "
                  class="sm-small disabled"
                  >Level {{ provider.client_level }}</span
                >
              </p>
              <span class="sm-header__controls-dropdown-btn-avatar">
                <img-loader v-if="avatar" :src="avatar" class="small" />
                <include-svg v-else :name="'icon-no-avatar'" />
              </span>
            </button>
            <ul
              v-if="menuPopup"
              v-click-outside="hidePopup"
              class="sm-header__controls-dropdown-list"
              :class="{
                admin: user.privileged_user
              }"
            >
              <li @click="goTo('account')">
                <include-svg :name="'icon-user'" />
                <span>{{ accountLinkText }}</span>
              </li>
              <li v-if="showLocationsLink()" @click="goTo('locations')">
                <include-svg :name="'icon-locations'" />
                <span>Locations</span>
              </li>

              <li v-if="user.privileged_user" @click="goTo('settings')">
                <include-svg :name="'icon-settings'" />
                <span>Business Settings</span>
              </li>
              <li v-if="user.privileged_user" @click="goTo('email-customers')">
                <include-svg :name="'icon-emails'" />
                <span>Email customers</span>
              </li>
              <li v-if="userAccount && userAccount.role == 'manager'" @click="goTo('clients')">
                <include-svg :name="'icon-user'" />
                <span>Operators</span>
              </li>

              <li
                v-if="
                  user.privileged_user ||
                    (clientFeatures.hasOwnProperty('show_help_link') &&
                      clientFeatures.show_help_link)
                "
                @click="openHelp"
              >
                <span>Help</span>
              </li>
              <!-- <li @click.stop="goTo('/login', true)"> -->
              <li @click.stop="logOut">
                <span>Sign out</span>
              </li>
            </ul>
          </div>
          <!-- <div
            v-if="user.privileged_user && (provider.client_level == 1 || userAccount.role == 'manager')"
            class="sm-header__controls-classic"
          >
            <btn-block
              :type="'small'"
              :text="'Switch to Classic view'"
              @click="switchToAdmin"
            />
          </div> -->
        </div>
        <div v-else class="sm-header__controls">
          <a
            v-for="link in controlLinks"
            :key="link.name"
            :href="link.href || link.name"
            class="sm-header__controls-link"
            :class="{
              disabled: link.disabled
            }"
            @click.prevent="goTo(link.href || `/${link.name}`)"
          >
            <include-svg :name="link.icon" />
            <span v-if="link.notices" class="sm-white">{{ link.notices }}</span>
          </a>
          <div class="sm-header__from-admin" @click="togglePopup()">
            <span class="sm-header__from-admin-avatar">
              <img-loader v-if="avatar" :src="avatar" class="small" />
              <include-svg v-else :name="'icon-no-avatar'" />
            </span>
            <div class="sm-header__from-admin-text">
              <text-truncate :text="name" />
              <span
                >You are logged in as {{ provider.client_level != 1 ? "customer" : "user" }}</span
              >
            </div>
            <button class="sm-header__from-admin-switch" @click.stop="switchToAdmin">
              <include-svg :name="'icon-back-bold'" />
              <span>Switch back</span>
            </button>
            <ul v-if="menuPopup" v-click-outside="hidePopup" class="sm-header__from-admin-menu">
              <li @click.stop="goTo('account')">
                <include-svg :name="'icon-user'" />
                <span>{{ accountLinkText }}</span>
              </li>
              <li
                v-if="
                  user.privileged_user ||
                    (clientFeatures.hasOwnProperty('show_help_link') &&
                      clientFeatures.show_help_link)
                "
                @click.stop="openHelp"
              >
                <span>Help</span>
              </li>
              <!-- <li @click="goTo('/login', true)"> -->
              <li @click="logOut">
                <span>Sign out</span>
              </li>
            </ul>
          </div>
        </div>
      </template>
      <div
        v-else
        v-click-outside="hideMobileMenu"
        class="sm-header__mobile"
        :class="mobileMenuActive ? 'active' : ''"
      >
        <a href="errors" class="sm-header__mobile-link" @click.prevent="showMobileMenu()"></a>
        <div class="sm-header__mobile-menu" :class="menuPopup ? 'open' : ''">
          <div v-if="isAdmin" class="sm-header__mobile-menu-admin">
            <span>You are logged in as {{ provider.client_level != 1 ? "customer" : "user" }}</span>
            <btn-block :type="'small'" :text="'Switch back'" @click="switchToAdmin" />
          </div>
          <div class="sm-header__mobile-menu-dropdown" @click="togglePopup()">
            <span class="sm-header__mobile-menu-dropdown-avatar">
              <img-loader v-if="avatar" :src="avatar" class="small" />
              <include-svg v-else :name="'icon-no-avatar'" />
            </span>
            <p>
              <text-truncate class="sm-header__mobile-menu-dropdown-name" :text="name" />
              <span
                v-if="
                  userAccount && userAccount.role == 'manager' && provider && provider.client_level
                "
                class="sm-small disabled"
                >Level {{ provider.client_level }}</span
              >
            </p>
            <include-svg :name="'icon-arrow-b'" />
          </div>
          <ul class="sm-header__mobile-menu-content">
            <li
              v-for="link in [...links, ...controlLinks.filter(link => !link.desktopOnly)]"
              :key="link.name"
              :class="{
                current: checkLinkCurrent(link),
                disabled: checkLinkInactive(link)
              }"
              @click="goTo(link.href || `/${link.name}`)"
            >
              <include-svg :name="link.icon" />
              <span class="sm-txt-capitalize">{{ link.title || link.name }}</span>
            </li>
            <li v-if="showErrorsLink" @click="goTo('/errors')">
              <include-svg :name="'icon-expl'" />
              <span class="sm-txt-capitalize">Errors</span>
            </li>
          </ul>
          <ul v-if="menuPopup" class="sm-header__mobile-menu-list" :class="isAdmin ? 'admin' : ''">
            <li @click="goTo('/account')">
              <include-svg :name="'icon-user'" />
              <span>{{ accountLinkText }}</span>
            </li>
            <li v-if="showLocationsLink()" @click="goTo('/locations')">
              <include-svg :name="'icon-locations'" />
              <span>Locations</span>
            </li>

            <li v-if="user.privileged_user" @click="goTo('/settings')">
              <include-svg :name="'icon-settings'" />
              <span>Business Settings</span>
            </li>
            <li v-if="user.privileged_user" @click="goTo('/email-customers')">
              <include-svg :name="'icon-emails'" />
              <span>Email customers</span>
            </li>
            <!-- v-if="this.providerFeatures.hasOwnProperty('show_help_link') && this.providerFeatures.show_help_link" -->
            <li
              v-if="
                user.privileged_user ||
                  (clientFeatures.hasOwnProperty('show_help_link') && clientFeatures.show_help_link)
              "
              @click="openHelp"
            >
              <span>Help</span>
            </li>
            <!-- <li @click="goTo('/login', true)"> -->
            <li @click="logOut">
              <span>Sign out</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <sm-spinner :show="showSpinner" />
  </header>
</template>

<script>
import IncludeSvg from "@/components/IncludeSvg";
import vClickOutside from "v-click-outside";
import { mapActions, mapGetters } from "vuex";
import urls from "@/settings/urls";

import ImgLoader from "@/components/ImgLoader";
import imageStore from "@/utils/image-store";
import Spinner from "@/components/Spinner";
import { ToUpper } from "@/utils/general-data";
import Urls from "@/settings/urls";
import { CheckIsStaffDemo } from "@/utils/roles";

export default {
  name: "header-elem",
  components: {
    "include-svg": IncludeSvg,
    "img-loader": ImgLoader,
    "sm-spinner": Spinner
  },
  data() {
    return {
      extraLinks: [
        {
          name: "notarize",
          title: "Notarize a document",
          icon: "icon-notarize",
          href: "account?tab=extras&notarize=1",
          planDisableField: "notarization_service_enabled",
          forPrivileged: false,
          forUsCitizensOnly: true
        }
      ],
      menuPopup: false,
      mobileMenuActive: false,
      unreadAnnouncements: 0,
      unreadNotifications: 0,
      unreadReminders: 0,
      showQR: false
    };
  },
  computed: {
    ...mapGetters({
      isMobile: "isMobile",
      isAdmin: "isAdmin",
      isDev: "isDev",
      user: "auth/user",
      userAccount: "account/userAccount",
      provider: "provider",
      showNotifs: "general/showNotifs",
      showAnnouns: "general/showAnnouns",
      announcements: "announcements",
      notifications: "notifications",
      showRems: "general/showRems",
      reminders: "reminders",
      userPlan: "account/userPlan",
      providerFeatures: "general/providerFeatures",
      clientFeatures: "general/clientFeatures"
    }),
    admin() {
      const nav = {
        links: [
          {
            name: "mail",
            title: "Mail Management",
            icon: "icon-mail-center-header",
            userDisableField: "can_access_mail_manager"
          },
          {
            name: "customers",
            title: this.provider.client_level != 1 ? "Customers" : "Users",
            icon: "icon-customers-header",
            roles: ["manager", "super_admin", "admin"]
          },
          {
            name: "reports",
            icon: "icon-reports-header",
            roles: ["manager", "super_admin"]
          }
        ],
        controlLinks: [
          {
            name: "announcements",
            icon: "icon-announcements",
            notices: this.unreadAnnouncements,
            roles: ["manager", "super_admin"]
          },
          {
            name: "reminders",
            icon: "icon-reminders-header",
            notices: this.unreadReminders
          },
          {
            name: "notifications",
            icon: "icon-notifs-header",
            notices: this.unreadNotifications
          },
          {
            name: "locations",
            icon: "icon-new-location-header"
          }
        ]
      };

      if (
        this.providerFeatures.hasOwnProperty("is_messaging_enabled") &&
        this.providerFeatures.is_messaging_enabled
      ) {
        nav.controlLinks.splice(1, 0, {
          name: "messages",
          icon: "icon-messages-header",
          notices: this.userAccount.total_new_messages
        });
      }
      return nav;
    },
    customer() {
      const nav = {
        links: [
          {
            name: "mail",
            icon: "icon-mail-header",
            userDisableField: "can_access_mail_manager"
          }
        ],
        controlLinks: [
        {
            name: "announcements",
            icon: "icon-announcements",
            notices: this.unreadAnnouncements
          },
        ]
      };

      if (
        this.clientFeatures.hasOwnProperty("is_messaging_enabled") &&
        this.clientFeatures.is_messaging_enabled
      ) {
        nav.controlLinks.push({
          name: "messages",
          icon: "icon-messages-header",
          notices: this.userAccount.total_new_messages
        });
      }
      return nav;
    },
    avatar() {
      const path = this.user.image_url ? imageStore(this.user.image_url) : null;

      return path;
    },
    logo() {
      return imageStore(this.provider.image_url);
    },
    name() {
      return this.user.privileged_user
        ? this.provider.name
        : `${this.user.first_name} ${this.user.last_name}`;
    },
    links() {
      if(this.user.type == "AdditionalRecipient"){
        return this.customer.links;
      }
      return [
        ...(this.user.privileged_user ? this.admin.links : this.customer.links),
        ...this.currentExtraLinks
      ];
    },
    currentExtraLinks() {
      let res = [];
      let arr = this.extraLinks
        .filter(link => link.forPrivileged == !!this.user.privileged_user)
        .filter(link => {
          const isUsCitizen = this.userAccount.country_id === 1;
          if (link.forUsCitizensOnly && !isUsCitizen) return false;
          return true;
        })
      if (arr.length)
        arr.forEach(link => {
          if (
            link.hasOwnProperty("planDisableField") &&
            Object.keys(this.userPlan).length &&
            this.userPlan[link.planDisableField]
          )
            res.push(link);
        });

      return res;
    },
    controlLinks() {
      return this.user.privileged_user
        ? this.admin.controlLinks.filter(link => {
            if (link.name === "locations") {
              return this.showLocationsLink();
            }
            return true;
          })
        : this.customer.controlLinks;
    },
    accountLinkText() {
      return this.user.privileged_user ? "My Administrator Account" : "My Account";
    },
    showSpinner() {
      return this.user.first_name && this.provider && this.provider.client_level ? false : true;
    },
    helpLink() {
      return this.user.privileged_user ? urls.links.admin_help : urls.links.customer_help;
    },
    showErrorsLink() {
      return this.userAccount.role == "manager";
    }
  },
  created() {
    this.showLocationsLink();
    if (this.user.privileged_user) {
      this.getNotifications();
      this.getReminders();
    }
  },
  methods: {
    ...mapActions({
      showNotifsBlock: "general/showNotifsBlock",
      showAnnounsBlock: "general/showAnnounsBlock",
      showRemsBlock: "general/showRemsBlock",
      setIsAdmin: "setIsAdmin",
      getReminders: "getReminders",
      getAnnouncements: "getAnnouncements",
      getNotifications: "getNotifications",
      getFeatures: "general/getFeatures",
      getClientFeatures: "general/getClientFeatures"
    }),
    toUpper: ToUpper,
    checkIsStaffDemo: CheckIsStaffDemo,
    togglePopup() {
      this.menuPopup = !this.menuPopup;
    },
    showPopup() {
      this.menuPopup = true;
    },
    hidePopup() {
      this.menuPopup = false;
    },
    showMobileMenu() {
      this.mobileMenuActive = true;
    },
    hideMobileMenu() {
      this.mobileMenuActive = false;
    },
    goTo(path, isDirect) {
      this.hidePopup();
      this.hideMobileMenu();

      if (path.includes("reminders")) {
        this.showRemsBlock();
        return;
      }

      if (path.includes("announcements") && this.userAccount.role == "subscriber") {
        this.showAnnounsBlock();
        return;
      }

      if (!path.includes("notifications")) {
        if (!isDirect)
          path = `/${this.user.privileged_user ? "admin" : "customer"}${
            path[0] == "/" ? "" : "/"
          }${path}`;

        this.$router.push(path);
      } else {
        if (this.userAccount.role == "manager") this.$router.push("notifications");
        else this.showNotifsBlock();
      }
    },
    openHelp() {
      let win = window.open(this.helpLink, "_blank");
      win.focus();
      this.hidePopup();
      this.hideMobileMenu();
    },
    switchToAdmin() {
      this.hidePopup();

      let adminAccess = localStorage.getItem("admin-access");

      if (adminAccess) {
        let adminAccessObj = JSON.parse(atob(adminAccess));
        this.setIsAdmin(false);
        localStorage.removeItem("admin-access");
        this.$router.push(
          `/admin/customers?access_token=${adminAccessObj.token}&refresh_token=${adminAccessObj.refresh_token}`
        );
      } else {
        window.location.href = `${window.location.origin}?classic=true`;
      }
    },
    checkLinkInactive(link) {
      return (
        link.hasOwnProperty("userDisableField") &&
        !this.user[link.userDisableField] &&
        !this.user.privileged_user &&
        this.user.level != 1 // disable nothing for lvl1
      );
    },
    checkLinkCurrent(link) {
      if (this.$route.name == "admin-email-customers") return false;
      return this.$route.name.includes(link.name);
    },
    showQRPopup() {
      this.showQR = true;
    },
    hideQRPopup() {
      this.showQR = false;
    },
    logOut() {
      // this.$router.push('/login');
      const wlp = window.location.port;
      const port = wlp === "8080" || wlp === "8081" ? ":" + wlp : "";
      const http = !port ? "https" : "http";
      const origin = window.location.hostname + port;
      window.location = Urls.logout + `?return_to=${http}://${origin}/customers/login`;
    },
    showLocationsLink() {
      return (
        this.user.privileged_user &&
        (this.userAccount.role == "manager" ||
          this.userAccount.is_organization_admin ||
          this.userAccount.role == "super_admin") // &&
          // this.currLocationInTheHardcodedList()
          // disabled currLocationInTheHardcodedList shows enhancements for all locations
      );
    }
    // currLocationInTheHardcodedList() {
    //   return this.isDev || this.checkIsStaffDemo(this.provider.id)
    // }
  },
  watch: {
    announcements: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val.hasOwnProperty("total_unread_announcements")) {
          this.unreadAnnouncements = val.total_unread_announcements;
        }
      }
    },
    reminders: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val.hasOwnProperty("total_unread_reminders")) {
          this.unreadReminders = val.total_unread_reminders;
        }
      }
    },
    notifications: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val.hasOwnProperty("total_unread_notifications")) {
          this.unreadNotifications = val.total_unread_notifications;
        }
      }
    },
    isStaffDemo(val) {
    },
    userAccount(val) {
      const {role} = val
      if(role && ["manager", "super_admin", "subscriber"].includes(role)) {
      this.getAnnouncements();
      }
    }
  },
  directives: {
    clickOutside: vClickOutside.directive
  }
};
</script>
